import { apiRequest } from "../../api/apiClient";
import type {
  Payment,
  CreatePaymentDto,
  UpdatePaymentDto,
  Currency,
  PaymentMethod,
  PaymentStatus,
} from "../../types/payment.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const paymentService = {
  // Payment CRUD operations
  getPayments: async (): Promise<Payment[]> => {
    const response = await apiRequest("/Payments", {
      method: "GET",
    });
    return handleResponse<Payment[]>(response);
  },

  getPaymentById: async (paymentId: number): Promise<Payment> => {
    const response = await apiRequest(`/Payments/${paymentId}`, {
      method: "GET",
    });
    return handleResponse<Payment>(response);
  },

  getPaymentsByApplicationId: async (
    applicationId: number,
  ): Promise<Payment[]> => {
    const response = await apiRequest(
      `/Payments/certificate-application/${applicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<Payment[]>(response);
  },

  createPayment: async (data: CreatePaymentDto): Promise<Payment> => {
    console.log("=== CREATE PAYMENT - Input Data ===");
    console.log("Data received:", data);

    // Always use FormData for this endpoint as backend expects multipart/form-data
    const formData = new FormData();
    formData.append(
      "certificateApplicationId",
      data.certificateApplicationId.toString(),
    );
    formData.append("lraPayment", data.lraPayment.toString());
    formData.append("operationalCost", data.operationalCost.toString());
    formData.append("currencyId", data.currencyId.toString());
    formData.append("statusId", data.statusId.toString());
    formData.append("paymentMode", data.paymentMode);
    formData.append(
      "paymentReferenceNumber",
      data.paymentReferenceNumber || "",
    );
    formData.append("revenueNumber", data.revenueNumber || "");

    // Append file if present (single file) - backend expects parameter named "document"
    if (data.documentPath && data.documentPath.length > 0) {
      formData.append("document", data.documentPath[0]);
      console.log("File to upload:", {
        name: data.documentPath[0].name,
        size: data.documentPath[0].size,
        type: data.documentPath[0].type,
      });
    } else {
      console.log("No document file to upload");
    }

    // Log FormData contents
    console.log("=== FormData Contents ===");
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`${key}:`, {
          name: value.name,
          size: value.size,
          type: value.type,
        });
      } else {
        console.log(`${key}:`, value);
      }
    }
    console.log("=== End FormData ===");

    const response = await apiRequest("/Payments", {
      method: "POST",
      body: formData,
    });
    return handleResponse<Payment>(response);
  },

  updatePayment: async (
    paymentId: number,
    data: UpdatePaymentDto,
  ): Promise<Payment> => {
    // Always use FormData for this endpoint
    const formData = new FormData();

    if (data.certificateApplicationId !== undefined) {
      formData.append(
        "certificateApplicationId",
        data.certificateApplicationId.toString(),
      );
    }
    if (data.lraPayment !== undefined) {
      formData.append("lraPayment", data.lraPayment.toString());
    }
    if (data.operationalCost !== undefined) {
      formData.append("operationalCost", data.operationalCost.toString());
    }
    if (data.currencyId !== undefined) {
      formData.append("currencyId", data.currencyId.toString());
    }
    if (data.statusId !== undefined) {
      formData.append("statusId", data.statusId.toString());
    }
    if (data.paymentMode !== undefined) {
      formData.append("paymentMode", data.paymentMode);
    }
    if (data.paymentReferenceNumber !== undefined) {
      formData.append("paymentReferenceNumber", data.paymentReferenceNumber);
    }
    if (data.revenueNumber !== undefined) {
      formData.append("revenueNumber", data.revenueNumber);
    }

    // Append file if present (single file) - backend expects parameter named "document"
    if (data.documentPath && data.documentPath.length > 0) {
      formData.append("document", data.documentPath[0]);
    }

    const response = await apiRequest(`/Payments/${paymentId}`, {
      method: "PUT",
      body: formData,
    });
    return handleResponse<Payment>(response);
  },

  deletePayment: async (paymentId: number): Promise<void> => {
    const response = await apiRequest(`/Payments/${paymentId}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },

  // Lookup data
  getCurrencies: async (): Promise<Currency[]> => {
    const response = await apiRequest("/Currencies", {
      method: "GET",
    });
    return handleResponse<Currency[]>(response);
  },

  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    const response = await apiRequest("/PaymentMethods", {
      method: "GET",
    });
    return handleResponse<PaymentMethod[]>(response);
  },

  getPaymentStatuses: async (): Promise<PaymentStatus[]> => {
    const response = await apiRequest("/PaymentStatuses", {
      method: "GET",
    });
    return handleResponse<PaymentStatus[]>(response);
  },
};
